import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connectLean } from 'lean-redux';
import { difference } from 'lodash';
import { AlertBox } from 'legacy-packages/ft-cb';
import SelectionBox from './SelectionBox';
import ClassContentWrapper from './ClassContentWrapper';
import dateTimeFormat from 'legacy-src/utility/DateTime';
import SelectClasses from './SelectClasses';

/**
 * Draws the My Classses selector
 * */
class ClassesSelector extends Component {
  static displayName = 'ClassesSelector';

  static propTypes = {
    className: PropTypes.string,
    label: PropTypes.string,
    selectedLabel: PropTypes.string,
    notificationText: PropTypes.string,
    items: PropTypes.array.isRequired,
    assignedToItems: PropTypes.array,
    isEmbeddedVideo: PropTypes.bool,
    hasNoClasses: PropTypes.bool,
    showHeader: PropTypes.bool,
    descriptionClassName: PropTypes.string,
  };

  static defaultProps = {
    className: '',
    label: 'Choose Classes',
    selectedLabel: 'Select classes',
    notificationText: '',
    items: [],
    assignedToItems: [],
    isEmbeddedVideo: false,
    hasNoClasses: false,
    showHeader: true,
    descriptionClassName: '',
  };

  static getDerivedStateFromProps({ selectedItems }) {
    return {
      selectedItems: selectedItems.map(String),
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      selectedItems: props.selectedItems,
    };
  }

  componentDidMount() {
    const {
      items,
      autoSelectedFirstItem,
      selectAllItems,
      selectedItems,
      isEmbeddedVideo,
    } = this.props;
    if (items.length === 1) {
      const firstItemId = items.length && items[0].id;
      /**
       * If item has not been assigned to a class and we have only 1 classes - auto-check the class
       * */
      this.setState({
        selectedItems: [firstItemId],
      });
      autoSelectedFirstItem(firstItemId);
    }

    if (items.length && selectedItems.length === 0 && isEmbeddedVideo) {
      selectAllItems();
    }
  }

  renderAssignedOnValue = (
    showAssigned,
    savedClasses,
    resourceShareData,
    cachedAssignData,
    section,
  ) =>
    showAssigned &&
    savedClasses.includes(parseInt(section.id, 10)) &&
    (resourceShareData[section.id] ||
      (cachedAssignData && cachedAssignData[section.id])) && (
      <div className="dropdown-select__assigned-on">
        <span aria-hidden="true">
          {dateTimeFormat(
            cachedAssignData[section.id] ||
              (resourceShareData[section.id] &&
                resourceShareData[section.id].assignedAt),
            'dateStandard',
            resourceShareData[section.id].timezone,
          )}
        </span>
      </div>
    );

  render() {
    const {
      children,
      className,
      label,
      description,
      onSelectAllClick,
      selectedLabel,
      assignedToItems,
      setSelectedItems,
      notificationText,
      handleBatchSelect,
      isAllItemSelected,
      isEmbeddedVideo,
      items: availableItems,
      hasNoClasses,
      resourceShareData,
      savedClasses,
      cachedAssignData,
      showHeader,
      descriptionClassName,
    } = this.props;
    const containerClassName = `classes-selector ${className}`;
    const { selectedItems } = this.state;

    let dropDownSelectCloseText = 'Choose Classes';
    if (selectedItems.length === 1) {
      ({ name: dropDownSelectCloseText } =
        availableItems.find((item) => item.id === selectedItems[0]) || {});
    } else if (selectedItems.length > 1) {
      dropDownSelectCloseText =
        selectedItems.length === availableItems.length
          ? 'All Classes'
          : 'Multiple Classes';
    }

    const showAssigned = isEmbeddedVideo && savedClasses.length > 0;
    const headerWrapper = showHeader ? (
      <h3
        className={cn('dark-header', { 'dark-bold-header': isEmbeddedVideo })}
      >
        {label}
      </h3>
    ) : null;
    const descriptionWrapper = (
      <p
        className={cn(
          { 'description-text': isEmbeddedVideo },
          descriptionClassName,
        )}
      >
        {description}
      </p>
    );

    if (hasNoClasses) {
      return (
        <div className={containerClassName}>
          {headerWrapper}
          {descriptionWrapper}
        </div>
      );
    }

    return (
      <div className={containerClassName}>
        {headerWrapper}
        {notificationText && (
          <AlertBox icon="circle_warn" color="orange">
            {notificationText}
          </AlertBox>
        )}
        {descriptionWrapper}
        {isEmbeddedVideo && (
          <ClassContentWrapper
            items={availableItems}
            handleBatchSelect={handleBatchSelect}
            setSelectedItems={setSelectedItems}
            isAllItemSelected={isAllItemSelected}
            selectedItems={selectedItems}
            dropDownSelectCloseText={dropDownSelectCloseText}
            showAssigned={showAssigned}
            resourceShareData={resourceShareData}
            savedClasses={savedClasses}
            cachedAssignData={cachedAssignData}
            renderAssignedOnValue={this.renderAssignedOnValue}
          />
        )}
        {!isEmbeddedVideo && (
          <SelectClasses
            items={availableItems}
            selectedItems={selectedItems}
            hasClasses={!!availableItems.length}
            label={selectedLabel}
            onButtonClick={onSelectAllClick}
            selectionBox={
              <SelectionBox
                className="min-h-76.2"
                items={availableItems}
                selectedItems={selectedItems}
                assignedToItems={assignedToItems}
                onItemClick={setSelectedItems}
              />
            }
          >
            {children}
          </SelectClasses>
        )}
      </div>
    );
  }
}

export default connectLean({
  getInitialState() {
    return {
      selectedItems: [],
    };
  },
  autoSelectedFirstItem(classId) {
    const itemId = Number(classId);
    this.setState({
      selectedItems: [itemId],
    });
  },
  onSelectAllClick(allSelected) {
    if (allSelected) {
      this.clearSelectedItems();
    } else {
      this.selectAllItems();
    }
  },
  clearSelectedItems() {
    this.setState({ selectedItems: [] });
  },
  handleBatchSelect() {
    this.isAllItemSelected()
      ? this.clearSelectedItems()
      : this.selectAllItems();
  },
  selectAllItems() {
    this.setState({
      selectedItems: this.props.items.map((item) => Number(item.id)),
    });
  },
  isAllItemSelected() {
    const { state, props } = this;
    return state.selectedItems.length === props.items.length;
  },
  setSelectedItems(classId) {
    const itemId = Number(classId);
    this.setState((props) => {
      const { selectedItems } = props;
      const allSelectedItems = selectedItems.find((x) => x === itemId)
        ? difference(selectedItems, [itemId])
        : [...selectedItems, itemId];

      return {
        selectedItems: allSelectedItems,
      };
    });
  },
})(ClassesSelector);
